import { useNavigate } from "react-router-dom";
import { playTTS } from "@/lib/tts";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Trash2, Star, Volume2, PlayCircle, Pencil } from "lucide-react";

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function WordDetailSheet({ word, open, onOpenChange, onToggleFavorite, onDelete, onEdit }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  if (!word) return null;

  const speak = () => {
    playTTS(word.word, user?.settings);
  };

  const goToContext = (c) => {
    if (!c.video_id) return;
    onOpenChange(false);
    navigate(`/player/${c.video_id}?t=${Math.floor(c.timestamp || 0)}`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="word-detail-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading flex items-center gap-2">
            <span data-testid="word-detail-word">{word.word}</span>
            <button onClick={speak} data-testid="word-detail-speak-button" className="text-muted-foreground hover:text-primary">
              <Volume2 className="w-4 h-4" />
            </button>
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-5 mt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>{word.transcription}</span>
            <span className="bg-secondary text-secondary-foreground text-xs uppercase px-2 py-0.5 rounded">{word.part_of_speech}</span>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">Перевод</p>
            <p className="text-lg font-semibold">{word.translation}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">Определение</p>
            <p className="text-sm leading-relaxed">{word.definition}</p>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">
              Контексты ({word.contexts?.length || 0})
            </p>
            <div className="space-y-2">
              {word.contexts?.map((c, i) => (
                <div key={i} className="bg-muted/60 rounded-lg p-3 text-sm" data-testid={`word-context-${i}`}>
                  <p className="italic">"{c.sentence}"</p>
                  {c.video_id ? (
                    <button
                      onClick={() => goToContext(c)}
                      data-testid={`word-context-timeline-${i}`}
                      className="flex items-center gap-1.5 text-xs text-primary hover:underline mt-1.5"
                    >
                      <PlayCircle className="w-3.5 h-3.5" />
                      <span className="truncate max-w-[220px]">{c.video_title}</span>
                      <span className="shrink-0">· {formatTime(c.timestamp || 0)}</span>
                    </button>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1.5">Добавлено вручную</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => onToggleFavorite(word)} data-testid="word-detail-favorite-button">
              <Star className={`w-4 h-4 mr-1 ${word.favorite ? "fill-yellow-400 text-yellow-400" : ""}`} />
              {word.favorite ? "В избранном" : "В избранное"}
            </Button>
            <Button variant="outline" size="icon" onClick={() => onEdit(word)} data-testid="word-detail-edit-button">
              <Pencil className="w-4 h-4" />
            </Button>
            <Button variant="destructive" size="icon" onClick={() => onDelete(word)} data-testid="word-detail-delete-button">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
